import { stringify } from 'hub-http/helpers/params';
import { List as ImmutableList } from 'immutable';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import shuffle from 'hs-lodash/shuffle';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Box } from 'foundations-components/transitional/components/Box';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { SelectableButton } from 'foundations-components/transitional/components/SelectableButton';
import UsageTracker from '../../../utils/UsageTracker';
import { useGetToolRecommendations } from '../queries/softwareDiscoveryHooks';
import { SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS } from '../../../constants/Navigation';
import { getRandomizedIndices } from '../utils/getRandomizedIndices';
import { jsx as _jsx } from "react/jsx-runtime";
const CARD_HEIGHT = 280;
const CARD_WIDTH = 240;
const ILLUSTRATION_SIZE = '100px';
const ToolCard = ({
  cardWidth,
  contentName,
  experimentUseCase,
  linkText,
  name,
  resourceTitle,
  title,
  toolSlug
}) => {
  const handleToolClick = () => UsageTracker.track('contentInteraction', {
    action: 'software-discovery-tool-click',
    contentName,
    linkText,
    resourceTitle,
    useCases: [experimentUseCase]
  });
  return /*#__PURE__*/_jsx(Box, {
    basis: cardWidth,
    shrink: 0,
    children: /*#__PURE__*/_jsx(Link, {
      "data-test-id": `tool-recommendation-card-${name}`,
      target: "_blank",
      to: {
        pathname: `/${SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS}/${toolSlug}`,
        search: stringify({
          useCases: experimentUseCase
        })
      },
      children: /*#__PURE__*/_jsx(SelectableButton, {
        block: true,
        height: CARD_HEIGHT,
        onClick: handleToolClick,
        paddingBlockStart: "500",
        selected: false,
        size: "large",
        title: title,
        children: /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(Illustration, {
            height: ILLUSTRATION_SIZE,
            name: name,
            width: ILLUSTRATION_SIZE
          })
        })
      })
    })
  });
};
const ToolRecommendationsForLearningContent = ({
  cardWidth = CARD_WIDTH,
  contentName,
  flexGap = '0',
  renderRecommendations = commonContent => commonContent,
  toolsToDisplay = 3,
  useCases: mayBeListUseCase,
  isRandomized = true
}) => {
  var _toolRecommendations$;
  const useCases = useMemo(() => {
    if (ImmutableList.isList(mayBeListUseCase)) {
      return mayBeListUseCase.toArray();
    }
    return mayBeListUseCase;
  }, [mayBeListUseCase]);
  const toolRecommendations = useGetToolRecommendations(useCases);
  const totalTools = (_toolRecommendations$ = toolRecommendations === null || toolRecommendations === void 0 ? void 0 : toolRecommendations.toolCardNames.length) !== null && _toolRecommendations$ !== void 0 ? _toolRecommendations$ : 0;
  const randomizedIndices = useMemo(() => {
    return getRandomizedIndices(totalTools, toolsToDisplay, isRandomized ? shuffle : arr => arr);
  }, [totalTools, toolsToDisplay, isRandomized]);
  useEffect(() => {
    if (toolRecommendations) {
      UsageTracker.track('interaction', {
        action: 'software-discovery-ui-viewed',
        useCases
      });
    }
  }, [toolRecommendations, useCases]);
  if (!toolRecommendations) return null;
  const {
    experimentUseCase,
    toolCardNames,
    toolNames,
    toolLabelTitles,
    toolSlug
  } = toolRecommendations;
  return renderRecommendations( /*#__PURE__*/_jsx(Flex, {
    alignItems: "stretch",
    flexWrap: "wrap",
    gap: flexGap,
    justifyContent: "space-around",
    children: randomizedIndices.map(toolIndex => /*#__PURE__*/_jsx(ToolCard, {
      cardWidth: cardWidth,
      contentName: contentName,
      experimentUseCase: experimentUseCase,
      linkText: I18n.text(toolLabelTitles[toolIndex]),
      name: toolCardNames[toolIndex],
      resourceTitle: toolNames[toolIndex],
      title: /*#__PURE__*/_jsx(Text, {
        paddingBlockEnd: "200",
        paddingBlockStart: "400",
        typography: "heading-100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: toolLabelTitles[toolIndex]
        })
      }),
      toolSlug: toolSlug[toolIndex]
    }, toolIndex))
  }));
};
export { ToolRecommendationsForLearningContent };